import { createTheme, responsiveFontSizes } from '@mui/material/styles';

import { BRAND_ACCENT, getThemeTokens } from './theme/tokens';

export { BRAND_ACCENT } from './theme/tokens';

const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const TYPOGRAPHY = {
  fontFamily: FONT_FAMILY,
  fontSize: 14,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h4: {
    fontFamily: FONT_FAMILY,
    fontSize: '2.5rem',
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: '-0.03em',
  },
  h5: {
    fontFamily: FONT_FAMILY,
    fontSize: '2rem',
    lineHeight: 1.05,
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  h6: {
    fontFamily: FONT_FAMILY,
    fontSize: '1.25rem',
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  subtitle1: {
    fontFamily: FONT_FAMILY,
    fontSize: '1rem',
    lineHeight: 1.35,
    fontWeight: 600,
  },
  subtitle2: {
    fontFamily: FONT_FAMILY,
    fontSize: '0.9375rem',
    lineHeight: 1.35,
    fontWeight: 600,
  },
  body1: {
    fontFamily: FONT_FAMILY,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    fontWeight: 400,
  },
  body2: {
    fontFamily: FONT_FAMILY,
    fontSize: '0.75rem',
    lineHeight: 1.5,
    fontWeight: 400,
  },
  caption: {
    fontFamily: FONT_FAMILY,
    fontSize: '0.6875rem',
    lineHeight: 1.45,
    fontWeight: 500,
  },
  button: {
    fontFamily: FONT_FAMILY,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.875rem',
    lineHeight: 1.2,
  },
};

export function createAppTheme(mode = 'light') {
  const tokens = getThemeTokens(mode);

  let theme = createTheme({
    typography: {
      ...TYPOGRAPHY,
    },
    shape: {
      borderRadius: 8,
    },
    palette: {
      mode: tokens.mode,
      primary: {
        main: tokens.colors.primary,
      },
      secondary: {
        main: BRAND_ACCENT,
      },
      success: {
        main: tokens.status.success,
      },
      info: {
        main: tokens.status.info,
      },
      warning: {
        main: tokens.status.warning,
      },
      error: {
        main: tokens.status.error,
      },
      background: {
        default: tokens.colors.background,
        paper: tokens.colors.surface,
      },
      text: {
        primary: tokens.colors.textPrimary,
        secondary: tokens.colors.textSecondary,
        disabled: tokens.colors.textMuted,
      },
      divider: tokens.colors.borderCard,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: tokens.colors.background,
            fontFamily: FONT_FAMILY,
          },
          body: {
            backgroundColor: tokens.colors.background,
            color: tokens.colors.textPrimary,
            fontFamily: FONT_FAMILY,
            transition: 'background-color 180ms ease, color 180ms ease',
          },
          '#root': {
            minHeight: '100vh',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            ...TYPOGRAPHY.button,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: FONT_FAMILY,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: FONT_FAMILY,
          },
          input: {
            fontFamily: FONT_FAMILY,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily: FONT_FAMILY,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: FONT_FAMILY,
            fontSize: TYPOGRAPHY.body1.fontSize,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: {
            fontFamily: FONT_FAMILY,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.colors.surface,
            border: `1px solid ${tokens.colors.borderCard}`,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.chrome.appBarBackground,
            color: tokens.chrome.appBarForeground,
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
  });

  theme.typography.h4 = {
    ...theme.typography.h4,
    [theme.breakpoints.up('md')]: {
      fontSize: '2.75rem',
    },
  };

  theme.typography.h5 = {
    ...theme.typography.h5,
    [theme.breakpoints.up('md')]: {
      fontSize: '2.2rem',
    },
  };

  theme.typography.h6 = {
    ...theme.typography.h6,
    [theme.breakpoints.up('md')]: {
      fontSize: '1.35rem',
    },
  };

  theme.typography.subtitle1 = {
    ...theme.typography.subtitle1,
    [theme.breakpoints.up('md')]: {
      fontSize: '1.0625rem',
    },
  };

  theme.typography.subtitle2 = {
    ...theme.typography.subtitle2,
    [theme.breakpoints.up('md')]: {
      fontSize: '1rem',
    },
  };

  theme.typography.body1 = {
    ...theme.typography.body1,
    [theme.breakpoints.up('md')]: {
      fontSize: '0.9375rem',
    },
  };

  theme.typography.body2 = {
    ...theme.typography.body2,
    [theme.breakpoints.up('md')]: {
      fontSize: '0.8125rem',
    },
  };

  theme.typography.caption = {
    ...theme.typography.caption,
    [theme.breakpoints.up('md')]: {
      fontSize: '0.75rem',
    },
  };

  theme.typography.button = {
    ...theme.typography.button,
    [theme.breakpoints.up('md')]: {
      fontSize: '0.9375rem',
    },
  };

  theme = responsiveFontSizes(theme);
  theme.bruno = tokens;
  theme.brunoTypography = TYPOGRAPHY;

  return theme;
}

export const scrollbarStyles = {
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: theme =>
      theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.24)' : 'rgba(0,0,0,0.15)',
    borderRadius: 2,
  },
  scrollbarWidth: 'thin',
  scrollbarColor: theme =>
    `${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.24)' : 'rgba(0,0,0,0.15)'} transparent`,
};

export default createAppTheme('light');
